/**
 * React Query hooks for the Retention admin screen. Same pattern
 * as useFeedback / useEscalations: mutations invalidate list +
 * stats so the queue stays live after force-actions.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  RetentionListParams,
  RetentionUpdateInput,
  fetchRetention,
  fetchRetentionOne,
  fetchRetentionStats,
  updateRetention
} from '@/api/retention';

export function useRetention(params: RetentionListParams) {
  return useQuery({
    queryKey: ['retention', params],
    queryFn: () => fetchRetention(params),
    refetchInterval: 60_000,
    placeholderData: prev => prev
  });
}

export function useRetentionStats() {
  return useQuery({
    queryKey: ['retentionStats'],
    queryFn: fetchRetentionStats,
    refetchInterval: 60_000
  });
}

export function useRetentionOne(id: string | null) {
  return useQuery({
    queryKey: ['retention', 'one', id],
    queryFn: () => fetchRetentionOne(id!),
    enabled: !!id
  });
}

export function useUpdateRetention() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: RetentionUpdateInput) => updateRetention(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['retention'] });
      qc.invalidateQueries({ queryKey: ['retentionStats'] });
    }
  });
}
